import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useAuthActions, useAuthModal } from 'authOld/hooks';
import { mapSignUpForm } from 'authOld/mappers';
import { ISignUpForm } from 'authOld/types';
import { HeaderModalOptions } from 'coreOld/constants';
import { ApiStatuses, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';
import { goTo } from 'coreOld/utils';

import { EmailWrapper } from './signUpFormStyle';
import SignUpFormView from './SignUpFormView';

const SignUpForm = () => {
  const [reCaptchaToken, setReCaptchaToken] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { openedAuthModal, openAuthModal, closeAuthModal } = useAuthModal();

  const reCaptchaRef = useRef(null);
  const { t } = useTranslation();
  const { clearNotification, showNotification } = useNotification();
  const { signUp } = useAuthActions();

  const onSubmit = () => async (data: ISignUpForm) => {
    if (!reCaptchaToken) {
      showNotification({
        message: t('errorReCaptchaRequired'),
        type: ApiStatuses.ERROR,
        position: NotificationPosition.MODAL,
      });

      return;
    }

    const { success, ssoEnabled, ssoRedirectUrl } = await signUp(
      mapSignUpForm(data, reCaptchaToken)
    );

    reCaptchaRef.current?.reset?.();
    setReCaptchaToken('');

    if (!success) {
      return;
    }

    if (ssoEnabled && ssoRedirectUrl) {
      goTo(ssoRedirectUrl);

      return;
    }

    showNotification({
      message: (
        <Trans
          i18nKey="errorSignUpInfo"
          values={{ email: data.email }}
          components={[<EmailWrapper key="email" />]}
        />
      ),
      type: ApiStatuses.INFO,
      position: NotificationPosition.MODAL,
    });

    setIsSubmitted(true);
  };

  const onClose = () => {
    clearNotification();
    closeAuthModal();
  };

  const goBack = () => {
    if (isSubmitted) {
      clearNotification();
      setIsSubmitted(false);
    }
  };

  return (
    <SignUpFormView
      isOpen={openedAuthModal === HeaderModalOptions.SIGN_UP}
      openAuthModal={openAuthModal}
      onClose={onClose}
      onSubmit={onSubmit}
      isSubmitted={isSubmitted}
      goBack={goBack}
      setReCaptchaToken={setReCaptchaToken}
      ref={reCaptchaRef}
    />
  );
};

export default SignUpForm;
